import { useSearchParams, useFetcher } from "react-router";
import { useState, FormEvent, useEffect, useId } from "react";
import { X } from "lucide-react";
import { FaCircleInfo } from "react-icons/fa6";
import { trackLegacyCheckout as track } from "~/utils/analytics";

type DiscountCodeInputProps = {
  groupQuote?: { ticketVariationID: string; additionalTickets: { eventTicketVariationID: string; recipientName: string }[] };
  eventId: string;
  eventTitle: string;
  appliedCode?: string;
  onApplyingChange?: (isApplying: boolean) => void;
};

export const DiscountCodeInput = ({
  groupQuote,
  eventId,
  eventTitle,
  appliedCode,
  onApplyingChange,
}: DiscountCodeInputProps) => {
  const [, setSearchParams] = useSearchParams();
  const [discountCode, setDiscountCode] = useState(appliedCode || "");
  const [isApplying, setIsApplying] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fetcher = useFetcher();
  const inputId = useId();
  const errorId = useId();

  useEffect(() => {
    onApplyingChange?.(isApplying);
  }, [isApplying, onApplyingChange]);

  useEffect(() => {
    setDiscountCode(appliedCode || "");
    setIsApplying(false);
    setValidationError(null);
  }, [appliedCode]);

  useEffect(() => {
    if (fetcher.data && fetcher.state === "idle" && isApplying) {
      const validationData = fetcher.data as {
        valid?: boolean; ok?: boolean;
        errorMessage: string | null;
      };

      const trimmedCode = discountCode.trim();
      if (validationData.valid || validationData.ok) {
        track("discount_code_applied", {
          event_id: eventId,
          event_title: eventTitle,
          code: trimmedCode,
        });
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("discountCode", trimmedCode);
            return next;
          },
          { replace: true, preventScrollReset: true },
        );
        setIsApplying(false);
      } else {
        track("discount_code_rejected", {
          event_id: eventId,
          event_title: eventTitle,
          code: trimmedCode,
        });
        setValidationError("This isn't a valid code for this plan.");
        setIsApplying(false);
      }
    }
  }, [
    fetcher.data,
    fetcher.state,
    isApplying,
    discountCode,
    eventId,
    eventTitle,
    setSearchParams,
  ]);

  const handleApply = (e: FormEvent) => {
    e.preventDefault();
    if (!discountCode.trim()) return;

    setIsApplying(true);
    setValidationError(null);

    if (groupQuote) {
      fetcher.submit({ ticketVariationID: groupQuote.ticketVariationID, additionalTickets: JSON.stringify(groupQuote.additionalTickets), discountCode: discountCode.trim() }, { method: "post", action: `/api/events/${eventId}/ticket-quote` });
      return;
    }
    fetcher.load(
      `/api/validate-discount?code=${encodeURIComponent(discountCode.trim())}&eventId=${eventId}`,
    );
  };

  const handleRemove = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("discountCode");
        return next;
      },
      { replace: true, preventScrollReset: true },
    );
  };

  const Label = (
    <p
      id={inputId + "-label"}
      className="font-sans text-sm font-bold text-butter-green leading-[1.4]"
    >
      Discount code
    </p>
  );

  if (appliedCode) {
    return (
      <div className="flex flex-col gap-1.5">
        {Label}
        <div className="flex items-center gap-3 rounded-[9px] border border-butter-cream bg-white px-3 py-3">
          <p className="min-w-0 flex-1 font-sans text-base font-normal text-butter-green [overflow-wrap:anywhere]">
            {appliedCode}
          </p>
          <button
            onClick={handleRemove}
            type="button"
            className="shrink-0 text-butter-green hover:opacity-70 transition-opacity cursor-pointer"
            aria-label="Remove discount code"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  const hasError = !!validationError;

  return (
    <form onSubmit={handleApply} className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <label
          htmlFor={inputId}
          className="font-sans text-sm font-bold text-butter-green leading-[1.4]"
        >
          Discount code
        </label>
        {hasError && (
          <span className="inline-flex items-center gap-1.5">
            <FaCircleInfo
              size={12}
              className="text-butter-pink"
              aria-hidden
            />
            <span
              id={errorId}
              role="alert"
              className="font-sans text-sm text-butter-pink leading-[1.4]"
            >
              {validationError}
            </span>
          </span>
        )}
      </div>
      <div
        className={
          "flex items-center gap-3 rounded-[9px] border bg-white px-3 py-3 transition-colors " +
          (hasError
            ? "border-butter-pink focus-within:border-butter-pink"
            : "border-butter-cream focus-within:border-butter-green/60")
        }
      >
        <input
          id={inputId}
          type="text"
          value={discountCode}
          onChange={(e) => {
            setDiscountCode(e.target.value.toUpperCase());
            setValidationError(null);
          }}
          placeholder="Enter code"
          disabled={isApplying}
          aria-invalid={hasError ? true : undefined}
          aria-describedby={hasError ? errorId : undefined}
          className="flex-1 min-w-0 appearance-none bg-transparent font-sans text-base text-butter-green outline-none placeholder:text-placeholder-text disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!discountCode.trim() || isApplying}
          className="shrink-0 font-sans text-base font-bold text-butter-green transition-opacity hover:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          {isApplying ? "Applying..." : "Apply"}
        </button>
      </div>
    </form>
  );
};
